import { useCallback, useRef } from 'react'
import { GridApi, RowDragEndEvent } from 'ag-grid-community'
import { useDragStyle } from '../../../containers/BulkSheetView/hooks/gridEvents/rowDrag'
import { useGridContext } from '../../../containers/BulkSheetView/hooks/useGridContext'
import { ProgressValue } from '../../../../domain/value-object/ProjectPlanSnapshot'
import { ProgressReportTableContent } from '../components/table/ProgressReportTable'
import { ReorderRow } from '../../../../applications/ports/versionReportVersionRepository'
import { VersionReportVersionPageRowEntity } from '../../../../domain/entity/version-report/VersionReportVersionEntity'

type Params = {
  gridApi: GridApi | undefined
  progressValues: ProgressValue[]
  setProgressValues: (values: ProgressValue[]) => void
  pageUuid: string
  rows: VersionReportVersionPageRowEntity[]
  applyReorderedRows: (
    pageUuid: string,
    reorderPayload: ReorderRow[]
  ) => Promise<void>
}

const INITIAL_CONTEXT: Record<string, unknown> = { draggableNodeId: undefined }

export const useProgressReportDragAndDrop = ({
  gridApi,
  progressValues,
  setProgressValues,
  pageUuid,
  rows,
  applyReorderedRows,
}: Params) => {
  // ローカル並び替えを先に反映した後、API レスポンスで rows が再フェッチされる場合は
  // ここで一度だけスキップする (caller 側の useEffect が依存配列に含む rows を見て再 fetch するため)
  const skipNextFetchRef = useRef(false)

  const { contextRef, updateContext } = useGridContext(gridApi, INITIAL_CONTEXT)
  const { onRowDragEnter, onRowDragMove, refreshDragStyle } =
    useDragStyle<ProgressReportTableContent>(gridApi, updateContext)

  const onRowDragEnd = useCallback(
    (event: RowDragEndEvent<ProgressReportTableContent>) => {
      refreshDragStyle()

      const movedUuid = event.node.data?.uuid
      const overIndex = event.overIndex
      if (!movedUuid || overIndex < 0) return

      const currentUuids = progressValues.map(v => v.uuid)
      const fromIndex = currentUuids.indexOf(movedUuid)
      if (fromIndex < 0 || fromIndex === overIndex) return

      const newUuids = [...currentUuids]
      const [moved] = newUuids.splice(fromIndex, 1)
      newUuids.splice(overIndex, 0, moved)

      const reordered = newUuids
        .map(uuid => progressValues.find(v => v.uuid === uuid))
        .filter((v): v is ProgressValue => !!v)

      const reorderPayload: ReorderRow[] = newUuids
        .map((targetUuid, newDisplayOrder) => {
          const row = rows.find(r => r.targetUuid === targetUuid)
          if (!row) return null
          if (row.displayOrder === newDisplayOrder) return null
          return {
            rowUuid: row.uuid,
            lockVersion: row.lockVersion,
            displayOrder: newDisplayOrder,
          }
        })
        .filter((p): p is ReorderRow => p !== null)

      skipNextFetchRef.current = true
      setProgressValues(reordered)
      if (reorderPayload.length > 0) {
        void applyReorderedRows(pageUuid, reorderPayload)
      }
    },
    [
      progressValues,
      setProgressValues,
      pageUuid,
      rows,
      applyReorderedRows,
      refreshDragStyle,
    ]
  )

  return {
    contextRef,
    onRowDragEnter,
    onRowDragMove,
    onRowDragEnd,
    onRowDragLeave: refreshDragStyle,
    skipNextFetchRef,
  }
}
